import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { WorkModel } from "../../../factories/works/models/work.model";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import {
  dueLabel,
  formatDate,
  peopleOf,
  splitDeadlines,
  statusGroup,
} from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import { PeopleClientTypeFilters, useReportFilters } from "../ui/ReportFilters";
import {
  AssigneeCell,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  LoadError,
  Loading,
  NUM,
  ReportPage,
  StatusPill,
  TableBox,
  TD,
  TH,
  useFocusRows,
  WorkLink,
} from "../ui/ReportUi";
import { useAllWorks } from "../useReportData";

/** The assignee as the table shows it (see AssigneeCell), for "Ask about this report". */
const assigneeText = (w: WorkModel) => {
  const people = peopleOf(w);
  if (!people[0]?.id) {
    return w.roleName ? `Role: ${w.roleName}` : "Not assigned";
  }
  return people.map((p) => p.name).join(", ");
};

type Tab = "overdue" | "week" | "month";

const TAB_OPTIONS: { value: Tab; label: string }[] = [
  { value: "overdue", label: "Overdue" },
  { value: "week", label: "Due this week" },
  { value: "month", label: "Next 30 days" },
];

const TITLES: Record<Tab, string> = {
  overdue: "Overdue work, most overdue first",
  week: "Due this week (until Sunday)",
  month: "Due in the next 30 days (after this week)",
};

export const DeadlinesReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const [tab, setTab] = useViewExtra<Tab>("tab", "overdue", TAB_OPTIONS, "");
  const works = useAllWorks({
    hideGreenStatus: true,
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    worktypeIds: filters.workTypeIds,
  });
  const lists = useMemo(() => splitDeadlines(works.data), [works.data]);
  const dueToday = lists.week.filter(
    (w) => dueLabel(w.dueDate) === "Today",
  ).length;
  const rows = lists[tab];

  const onExport = () =>
    exportDataToExcel(
      rows.map((w) => ({
        Work: w.workName,
        Client: w.customerName,
        "Work type": w.workType,
        Assignee: peopleOf(w)
          .map((p) => p.name)
          .join(", "),
        Status: w.jobSubStatus || w.jobStatus,
        "Due date": formatDate(w.dueDate),
        When: dueLabel(w.dueDate),
      })),
      `Deadlines - ${tab}`,
    );

  const value = (n: number) => (works.isLoading ? "…" : n);

  const shownRows = useFocusRows(TITLES[tab], rows, (w) => w.workId);
  const { pageRows, pager } = usePagedRows(shownRows);

  const ask = useMemo(
    () => ({
      title: "Deadlines",
      path: "/reports/deadlines",
      description: "Work that's overdue or coming due. A week ends on Sunday.",
      filters: filtersText([
        `Showing: ${TITLES[tab]}`,
        !access.firmWide && "Only your own work",
      ]),
      kpis: [
        { label: "Overdue", value: String(lists.overdue.length) },
        { label: "Due this week", value: String(lists.week.length) },
        { label: "Due today", value: String(dueToday) },
        {
          label: "Next 30 days (after this week)",
          value: String(lists.month.length),
        },
      ],
      tables: [
        askTable(
          TITLES[tab],
          rows,
          [
            {
              key: "work",
              label: "Work",
              kind: "text",
              value: (w) => w.workName,
            },
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (w) => w.customerName,
            },
            {
              key: "workType",
              label: "Work type",
              kind: "text",
              value: (w) => w.workType,
            },
            {
              key: "assignee",
              label: "Assignee",
              kind: "text",
              value: assigneeText,
            },
            {
              key: "status",
              label: "Status",
              kind: "text",
              value: (w) => w.jobSubStatus || w.jobStatus,
            },
            {
              key: "due",
              label: "Due date",
              kind: "date",
              value: (w) => formatDate(w.dueDate),
            },
            {
              key: "when",
              label: tab === "overdue" ? "Late by" : "Time left",
              kind: "text",
              value: (w) => dueLabel(w.dueDate),
            },
          ],
          (w) => w.workId,
          (w) => `/work/${w.workId}`,
        ),
      ],
    }),
    [tab, rows, lists, dueToday, access.firmWide],
  );

  return (
    <ReportPage
      title={"Deadlines"}
      description={
        "Work that's overdue or coming due. Click a box to switch the list. A week ends on Sunday."
      }
      onExport={rows.length ? onExport : undefined}
      ask={works.isLoading ? undefined : ask}
      askSuggestions={[
        "Which clients have the most overdue work?",
        "Who has the most work due this week?",
        "What is the most overdue work item?",
      ]}
      filters={
        <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
      }>
      {works.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Overdue"}
          value={value(lists.overdue.length)}
          note={"Past the due date"}
          tone={"red"}
          active={tab === "overdue"}
          onClick={() => setTab("overdue")}
        />
        <Kpi
          label={"Due this week"}
          value={value(lists.week.length)}
          note={`Today to Sunday · ${dueToday} due today`}
          active={tab === "week"}
          onClick={() => setTab("week")}
        />
        <Kpi
          label={"Next 30 days"}
          value={value(lists.month.length)}
          note={"After this week"}
          active={tab === "month"}
          onClick={() => setTab("month")}
        />
      </KpiGrid>

      <Card title={TITLES[tab]}>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !rows.length ? (
          <Empty
            text={
              tab === "overdue"
                ? "Nothing is overdue."
                : "Nothing is due in this period."
            }
          />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Work
                </th>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={TH}>
                  Work type
                </th>
                <th scope={"col"} className={TH}>
                  Assignee
                </th>
                <th scope={"col"} className={TH}>
                  Status
                </th>
                <th scope={"col"} className={TH}>
                  Due date
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  {tab === "overdue" ? "Late by" : "Time left"}
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((w) => {
                const label = dueLabel(w.dueDate);
                return (
                  <tr key={w.workId}>
                    <td className={TD}>
                      <WorkLink id={w.workId} name={w.workName} />
                    </td>
                    <td className={TD}>{w.customerName}</td>
                    <td className={TD}>{w.workType}</td>
                    <td className={TD}>
                      <AssigneeCell people={peopleOf(w)} role={w.roleName} />
                    </td>
                    <td className={TD}>
                      <StatusPill
                        group={statusGroup(w)}
                        label={w.jobSubStatus || w.jobStatus}
                      />
                    </td>
                    <td className={TD}>{formatDate(w.dueDate)}</td>
                    <td
                      className={`${TD} ${NUM} font-extrabold ${
                        tab === "overdue"
                          ? "text-[#B42318]"
                          : label === "Today"
                          ? "text-[#8A4100]"
                          : "text-[#3F4757]"
                      }`}>
                      {label}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableBox>
        )}
      </Card>
    </ReportPage>
  );
};
